import moment from 'moment-timezone'

export const PROVIDER_AVAILABILITY_PILLS_STATUSES = {
  AVAILABLE_TODAY: 'Available today',
  FIND_NEXT_AVAILABILITY: 'Find next availability',
  AVAILABLE_TOMORROW: 'Available tomorrow',
  AVAILABLE_NEXT_WEEK: 'Available next week',
}

/**
 * Determines the description for the provider's availability based on the date.
 *
 * @param {string} date - The date string from the provider's TimeSlot.
 * @returns {string} - A string representing the availability message.
 */
// @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
const getAvailabilityDescription = (date) => {
  // Early return if date is not provided
  if (!date) {
    return PROVIDER_AVAILABILITY_PILLS_STATUSES.FIND_NEXT_AVAILABILITY
  }
  const today = moment()
  const appointmentDate = moment(date)
  const tomorrow = moment().add(1, 'days')
  const endOfWeek = moment().endOf('week')
  const endOfNextWeek = moment().add(1, 'week').endOf('week')
  if (appointmentDate.isValid() && appointmentDate.isSame(today, 'day')) {
    return PROVIDER_AVAILABILITY_PILLS_STATUSES.AVAILABLE_TODAY
  }
  if (appointmentDate.isValid() && appointmentDate.isSame(tomorrow, 'day')) {
    return PROVIDER_AVAILABILITY_PILLS_STATUSES.AVAILABLE_TOMORROW
  }
  if (appointmentDate.isValid() && appointmentDate.isBefore(endOfWeek)) {
    return `Available ${appointmentDate.format('dddd')}`
  }
  if (appointmentDate.isValid() && appointmentDate.isBefore(endOfNextWeek)) {
    return PROVIDER_AVAILABILITY_PILLS_STATUSES.AVAILABLE_NEXT_WEEK
  }
  return PROVIDER_AVAILABILITY_PILLS_STATUSES.FIND_NEXT_AVAILABILITY
}

/**
 * Filters and formats a list of providers by their full names, including prefix and suffix,
 * while also keeping separate values for first name, last name, prefix, and suffix.
 * It also considers that 'TimeSlot' and 'HasPastVisit' are properties at the same level as 'Provider'.
 *
 * @param {Array} data - The array of objects containing provider details along with timeslot and past visit info.
 * @returns {Array} - Array of formatted provider data.
 *
 * @example
 * const formattedProviders = getProvidersListByName(data);
 */
export const getProvidersListByName = (data = []) =>
  data.map((item = {}) => {
    // @ts-expect-error TS2339 -- MIGRATION: not approved, fix when encountered
    const { Provider, TimeSlot, HasPastVisit } = item
    const fullName = [Provider.Prefix, Provider.FirstName, Provider.LastName, Provider.Suffix]
      .filter(Boolean)
      .join(' ')
      .trim()
    return {
      id: Provider.Id,
      providerId: Provider.Id,
      fullName,
      firstName: Provider.FirstName,
      lastName: Provider.LastName,
      prefix: Provider.Prefix || '',
      suffix: Provider.Suffix || '',
      profileImage: Provider.ImageUrl,
      date: TimeSlot?.Date,
      times: TimeSlot?.Times || [],
      Name: getAvailabilityDescription(TimeSlot?.Date),
      hasPastVisit: HasPastVisit,
    }
  })

// @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
export const filterProviders = (providers, searchQuery, filterPastVisits, selectedTimeRange) => {
  // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
  const normalizeString = (str) => str.toLowerCase().replace(/\s+/g, '')

  // Helper to check if a time is within a range
  // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
  const isTimeInRange = (time, range) => {
    // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
    const [start, end] = range.split('-').map((t) => t.trim())

    // Convert start and end times to 24-hour format
    const startTime = moment(start, ['h:mma', 'h:mmA']).format('HH:mm') // e.g., "10:00 PM" -> "22:00"
    const endTime = moment(end, ['h:mma', 'h:mmA']).format('HH:mm') // e.g., "11:00 PM" -> "23:00"

    const formattedTime = moment(time, 'HH:mm').format('HH:mm') // Keep time in 24-hour format

    // Check if time falls within the range
    return formattedTime >= startTime && formattedTime < endTime
  }

  // Step 1: Filter by past visits if enabled
  // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
  let filteredProviders = providers.filter((providerWrapper) => {
    if (filterPastVisits) {
      return providerWrapper.HasPastVisit === true
    }
    return true
  })

  // Step 2: Filter by search query
  if (searchQuery && searchQuery.trim().length >= 2) {
    const normalizedSearchQuery = normalizeString(searchQuery)
    // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
    filteredProviders = filteredProviders.filter((providerWrapper) => {
      const { Prefix, FirstName, LastName, Suffix } = providerWrapper.Provider
      const fullName = `${Prefix || ''} ${FirstName} ${LastName} ${Suffix || ''}`.trim()
      return normalizeString(fullName).includes(normalizedSearchQuery)
    })
  }

  // Step 3: Filter time slots within the selected range
  if (selectedTimeRange && !selectedTimeRange.toLowerCase().includes('all day')) {
    filteredProviders = filteredProviders
      // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
      .map((providerWrapper) => {
        const { TimeSlotCollection } = providerWrapper

        // Filter TimeSlotCollection to keep only valid times within the range
        // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
        const updatedTimeSlotCollection = TimeSlotCollection.map((slot) => ({
          ...slot,
          // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
          Times: slot.Times.filter((time) => isTimeInRange(time, selectedTimeRange)),
          // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
        })).filter((slot) => slot.Times.length > 0) // Remove empty slots

        return updatedTimeSlotCollection.length > 0 ?
            { ...providerWrapper, TimeSlotCollection: updatedTimeSlotCollection }
          : null
      })
      .filter(Boolean) // Remove providers with no valid time slots
  }

  // Step 4: Sort providers by date and time
  // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
  filteredProviders.sort((a, b) => {
    // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
    const compareTimes = (timeA, timeB) => {
      const [hourA, minuteA] = timeA.split(':').map(Number)
      const [hourB, minuteB] = timeB.split(':').map(Number)
      return hourA !== hourB ? hourA - hourB : minuteA - minuteB
    }

    const dateA = a.TimeSlotCollection?.[0]?.Date || null
    const dateB = b.TimeSlotCollection?.[0]?.Date || null
    const timeA = a.TimeSlotCollection?.[0]?.Times?.[0] || '23:59'
    const timeB = b.TimeSlotCollection?.[0]?.Times?.[0] || '23:59'

    if (!dateA && !dateB) return a.Provider.LastName.localeCompare(b.Provider.LastName)
    if (!dateA) return 1
    if (!dateB) return -1

    // @ts-expect-error TS2362 -- MIGRATION: not approved, fix when encountered
    const dateComparison = new Date(dateA) - new Date(dateB)
    if (dateComparison !== 0) return dateComparison

    return compareTimes(timeA, timeB)
  })

  return filteredProviders
}

/**
 * Conditionally builds the search results subtitle copy given a list of search results and filter and query state
 *
 * @param {Object[]} availableProviders Providers the patient is able to book an appointment with
 * @param {boolean} isFilterOn Boolean indicating if patient is filtering for previously seen providers
 * @param {string} searchQuery String representing patient's search query
 * @returns {string} Conditionally determined subtitle copy
 */

// @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
export const transformTimeToAmPm = (time) => moment(time, 'HH:mm').format('h:mma') // e.g., "18:50" -> "6:50 pm"

/**
 * Conditionally builds the search results main title copy given a list of search results
 *
 * @param {Object[]} availableProviders Providers the patient is able to book an appointment with
 * @returns {string} Conditionally determined title copy
 */
// @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
export const getSearchResultHeaderMainTitle = (availableProviders) => {
  const count = availableProviders.length

  if (count === 0) {
    return 'No providers found'
  }

  return `${count} provider${count !== 1 ? 's' : ''} found`
}

/**
 * Conditionally builds the search results subtitle copy given a list of search results and filter and query state
 *
 * @param {Object[]} availableProviders Providers the patient is able to book an appointment with
 * @param {boolean} isFilterOn Boolean indicating if patient is filtering for previously seen providers
 * @param {string} searchQuery String representing patient's search query
 * @returns {string} Conditionally determined subtitle copy
 */
// @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
export const getSearchResultHeaderSubtitle = (availableProviders, isFilterOn, searchQuery) => {
  if (!availableProviders.length) {
    return 'There are no providers available based on your search. Clear your search to view all available providers.'
  }

  if (searchQuery.length > 0 || isFilterOn) {
    return 'Showing visits based on your search'
  }

  return 'Showing all providers in your state'
}

// @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
export const cleanProviderName = (name) => {
  if (!name) return ''
  return name.replace(/null/gi, '').replace(/\s+/g, ' ').trim()
}
